/* ==========================================================================
   Nature Lane Nutrition — storefront behaviour
   No dependencies. Every interaction degrades to a working, non-JS fallback.
   ========================================================================== */

(function () {
  'use strict';

  var theme = window.theme || {};
  var routes = theme.routes || {};
  var strings = theme.strings || {};
  var config = theme.settings || {};

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  /* ------------------------------------------------------------------
     Header — mobile navigation
     ------------------------------------------------------------------ */

  function initHeader() {
    var header = $('[data-header]');
    if (!header) return;

    var toggle = $('[data-nav-toggle]', header);
    var nav = $('[data-nav]', header);

    function measure() {
      header.style.setProperty('--header-offset', header.getBoundingClientRect().bottom + 'px');
    }

    function close() {
      header.classList.remove('is-open');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    if (toggle && nav) {
      toggle.addEventListener('click', function () {
        var open = header.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) measure();
      });

      nav.addEventListener('click', function (event) {
        if (event.target.closest('a')) close();
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') close();
      });

      window.addEventListener('resize', function () {
        if (window.innerWidth > 899) close();
        else if (header.classList.contains('is-open')) measure();
      });
    }

    measure();
    window.addEventListener('scroll', measure, { passive: true });
  }

  /* ------------------------------------------------------------------
     FAQ accordion — one panel open at a time, matching the design
     ------------------------------------------------------------------ */

  function initAccordions() {
    $$('[data-accordion]').forEach(function (list) {
      var buttons = $$('[data-accordion-toggle]', list);

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          var wasOpen = button.getAttribute('aria-expanded') === 'true';
          buttons.forEach(function (other) {
            other.setAttribute('aria-expanded', 'false');
          });
          button.setAttribute('aria-expanded', wasOpen ? 'false' : 'true');
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Cart count + toast
     ------------------------------------------------------------------ */

  var toastTimer = null;

  function showToast(message) {
    var toast = $('#CartToast');
    if (!toast) return;

    $('.cart-toast__text', toast).textContent = message;
    toast.hidden = false;
    window.requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });

    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      toast.classList.remove('is-visible');
      window.setTimeout(function () {
        toast.hidden = true;
      }, 220);
    }, 4200);
  }

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach(function (node) {
      node.textContent = count;
      node.hidden = count === 0;
    });
  }

  function refreshCart() {
    return fetch(routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        return cart;
      })
      .catch(function () {
        /* Count stays as rendered; the cart page remains the source of truth. */
      });
  }

  /* ------------------------------------------------------------------
     Buy box — plan selector, variant pricing, add to cart
     ------------------------------------------------------------------ */

  function initBuyBox(root) {
    var form = $('[data-buy-form]', root);
    if (!form) return;

    var data = {};
    var dataEl = $('[data-buy-data]', root);
    if (dataEl) {
      try {
        data = JSON.parse(dataEl.textContent);
      } catch (error) {
        data = {};
      }
    }

    var submit = $('[data-buy-submit]', form);
    var submitText = submit ? $('[data-buy-submit-text]', submit) : null;
    var errorEl = $('[data-buy-error]', root);
    var variantInput = $('[data-variant-id]', form);
    var variantSelect = $('[data-variant-select]', form);
    var planInputs = $$('[data-plan-input]', form);
    var addedLabel = strings.added || 'Added to cart';
    var resetTimer = null;

    function currentVariant() {
      return variantInput ? data[variantInput.value] : null;
    }

    function selectedPlan() {
      for (var i = 0; i < planInputs.length; i++) {
        if (planInputs[i].checked) return planInputs[i];
      }
      return null;
    }

    function pricingFor(variant, planId) {
      if (!variant) return null;
      return planId ? (variant.plans || {})[planId] : variant.once;
    }

    function render() {
      var variant = currentVariant();
      var plan = selectedPlan();

      /* A plan offered on one variant may not exist on another. Fall back to
         the one-time option rather than submitting an invalid selling plan. */
      if (plan && plan.value && !pricingFor(variant, plan.value)) {
        for (var i = 0; i < planInputs.length; i++) {
          if (!planInputs[i].value) {
            planInputs[i].checked = true;
            plan = planInputs[i];
            break;
          }
        }
      }

      /* Per-plan pricing for the active variant. */
      planInputs.forEach(function (input) {
        var wrapper = input.closest('[data-plan]');
        if (!wrapper) return;

        wrapper.classList.toggle('is-selected', input.checked);

        var priceEl = $('[data-plan-price]', wrapper);
        var detailEl = $('[data-plan-detail]', wrapper);
        var pricing = pricingFor(variant, input.value);

        /* Variants without this plan keep their server-rendered markup. */
        wrapper.hidden = Boolean(input.value) && variant && !pricing;

        if (pricing) {
          if (priceEl && pricing.price) priceEl.innerHTML = pricing.price;
          if (detailEl && pricing.detail) detailEl.textContent = pricing.detail;
        }
      });

      if (!submit || !submitText) return;

      if (!variant || !variant.available) {
        submit.disabled = true;
        submitText.textContent = variant ? strings.soldOut || 'Sold out' : strings.unavailable || 'Unavailable';
        return;
      }

      submit.disabled = false;
      var pricing = plan && plan.value ? (variant.plans || {})[plan.value] : variant.once;
      submitText.textContent = (pricing && pricing.cta) || strings.addToCart || 'Add to cart';
    }

    function clearAddedState() {
      if (!submit) return;
      submit.classList.remove('is-added');
      render();
    }

    planInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        window.clearTimeout(resetTimer);
        clearAddedState();
      });
    });

    if (variantSelect && variantInput) {
      variantSelect.addEventListener('change', function () {
        variantInput.value = variantSelect.value;
        window.clearTimeout(resetTimer);
        clearAddedState();
      });
    }

    form.addEventListener('submit', function (event) {
      if (!config.ajaxCart) return;
      event.preventDefault();

      if (errorEl) errorEl.hidden = true;
      if (submit) submit.disabled = true;

      fetch(routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (response) {
          return response.json().then(function (body) {
            if (!response.ok) throw body;
            return body;
          });
        })
        .then(function (item) {
          if (submit && submitText) {
            submit.classList.add('is-added');
            submitText.textContent = addedLabel;
            submit.disabled = false;
          }
          showToast((item && item.title ? item.title : addedLabel) + ' — ' + addedLabel);
          refreshCart();
          window.clearTimeout(resetTimer);
          resetTimer = window.setTimeout(clearAddedState, 6000);
        })
        .catch(function (error) {
          if (submit) submit.disabled = false;
          var message = (error && (error.description || error.message)) || strings.cartError || 'Something went wrong.';
          if (errorEl) {
            errorEl.textContent = message;
            errorEl.hidden = false;
          } else {
            showToast(message);
          }
        });
    });

    render();
  }

  /* ------------------------------------------------------------------
     Cart page quantity steppers
     ------------------------------------------------------------------ */

  function initQuantity() {
    $$('[data-qty]').forEach(function (widget) {
      var input = $('input', widget);
      if (!input) return;

      $$('[data-qty-step]', widget).forEach(function (button) {
        button.addEventListener('click', function () {
          var step = parseInt(button.getAttribute('data-qty-step'), 10) || 0;
          var next = Math.max(0, (parseInt(input.value, 10) || 0) + step);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      input.addEventListener('change', function () {
        var form = input.closest('form');
        if (form) form.submit();
      });
    });
  }

  /* ------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------ */

  function init(scope) {
    initHeader();
    initAccordions();
    initQuantity();
    $$('[data-buy-box]', scope || document).forEach(initBuyBox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* Theme editor: re-bind after a section is re-rendered. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
